// sclMapper.ts — Mapeo de nodos MMS hacia un archivo SCL (CID/SCD).
// Implementa el proceso PROMPT_UNIVERSAL v2 (Etapas 1-3) en el navegador,
// solo para datasets MMS (ReportControl). GOOSE/SV quedan para una fase futura.
import type { NbSignal } from "./jsonContracts";

const SCL_NS = "http://www.iec.ch/61850/2003/SCL";

// Clases IEC 61850 conocidas (respaldo si DataTypeTemplates no las trae todas)
const IEC_LN_FALLBACK = ["XCBR","XSWI","CSWI","CILO","PTRC","PTOC","PTUV","PTOV",
  "MMXU","MMXN","MSQI","RDRE","CALH","LLN0","LPHD","GAPC","SPVC","SIMG","SCBR",
  "PIOC","PDIS","PDIF","PTUF","PTOF","PDOP","PDUP","PPAM","PSDE","PHAR","PFRC",
  "RREC","RBRF","RFLO","RPSB","RSYN","RDIR","ATCC","YLTC","TCTR","TVTR","GGIO",
  "MMTR","MMDC","MHAI","MHAN","MSTA","ITCI","IHMI","ITMI","LCCH","LGOS","LSVS"];

export interface MapSummary {
  xml: string;
  iedResults: { iedName: string; ldInst: string; createdDatasets: string[]; appendedDatasets: string[]; fcdaCount: number }[];
  fcdaCount: number;
  skipped: { signal: string; node: string; reason: string }[];
}

// Parámetros del ReportControl por dataset (defaults del PROMPT_UNIVERSAL v2)
export interface RcParams { buffered: boolean; trgOps: Record<string, boolean>; optFields: Record<string, boolean>; }
export const DEFAULT_TRGOPS: Record<string, boolean> = { dchg: true, qchg: true, dupd: true };
export const DEFAULT_OPTFIELDS: Record<string, boolean> = {
  reasonCode: true, seqNum: true, dataSet: true, dataRef: true,
  configRef: true, timeStamp: true, entryID: true,
};

// ── Etapa 1 · Paso 13 (extendido): FC por dataset destino / clase ──
function fcFor(dsName: string, lnClass: string): string {
  if (/MEAS|MED/i.test(dsName)) return "MX";
  if (/^M/.test(lnClass) && lnClass !== "LLN0") return "MX";
  return "ST";
}

// Sugerencia de dataset destino por clase del nodo (editable por el usuario)
export function suggestDataset(mmsNode: string): string {
  const dec = decomposeMms(mmsNode, IEC_LN_FALLBACK);
  if (!dec) return "GEN";
  const c = dec.lnClass;
  if (/^M/.test(c) && c !== "LLN0") return "MEAS";
  if (/^P/.test(c) || ["RREC","RBRF","RPSB","RDIR","RFLO"].includes(c)) return "PROT";
  if (["XCBR","XSWI","CSWI","CILO","RSYN","ATCC","YLTC"].includes(c)) return "CTRL";
  if (["LPHD","LCCH","LGOS","LSVS","ITCI","ITMI"].includes(c)) return "COMM";
  return "GEN";
}

// ── Inventario del SCL: IEDs con sus LDevices y datasets existentes ──
export interface SclIedInfo { name: string; lDevices: string[]; }
export interface SclInventory { ieds: SclIedInfo[]; datasets: string[]; }

export function listScl(xmlText: string): SclInventory {
  const doc = new DOMParser().parseFromString(xmlText, "application/xml");
  if (doc.getElementsByTagName("parsererror").length) throw new Error("XML inválido");
  const root = doc.documentElement;
  const ieds: SclIedInfo[] = [];
  const datasets = new Set<string>();
  for (const ied of Array.from(root.children).filter(e => e.localName === "IED")) {
    const name = ied.getAttribute("name") ?? "";
    const lds: string[] = [];
    for (const ap of Array.from(ied.children).filter(e => e.localName === "AccessPoint")) {
      for (const server of Array.from(ap.children).filter(e => e.localName === "Server")) {
        for (const ld of Array.from(server.children).filter(e => e.localName === "LDevice")) {
          const inst = ld.getAttribute("inst"); if (inst) lds.push(inst);
          const ln0 = Array.from(ld.children).find(e => e.localName === "LN0");
          if (ln0) for (const ds of Array.from(ln0.children).filter(e => e.localName === "DataSet")) {
            const n = ds.getAttribute("name"); if (n) datasets.add(n);
          }
        }
      }
    }
    if (name) ieds.push({ name, lDevices: lds });
  }
  return { ieds, datasets: [...datasets].sort() };
}

// IED sugerido para un nodo: similitud entre el LDevice del nodo y los del IED.
// Exacta > sin mayúsculas > parcial (prefijo/contenido) > primer IED del archivo.
export function suggestIed(mmsNode: string, ieds: SclIedInfo[]): string | undefined {
  if (!ieds.length) return undefined;
  const ld = (mmsNode || "").split("/")[0]?.trim();
  if (ld) {
    let hit = ieds.find(i => i.lDevices.includes(ld));
    if (hit) return hit.name;
    const ldLow = ld.toLowerCase();
    hit = ieds.find(i => i.lDevices.some(d => d.toLowerCase() === ldLow));
    if (hit) return hit.name;
    hit = ieds.find(i => i.lDevices.some(d => {
      const dl = d.toLowerCase();
      return dl.startsWith(ldLow) || ldLow.startsWith(dl) || dl.includes(ldLow) || ldLow.includes(dl);
    }));
    if (hit) return hit.name;
  }
  return ieds[0].name;
}

interface Decomp { ldInst: string; prefix: string; lnClass: string; lnInst: string; doName: string; }

// ── Etapa 1 · Pasos 5-12: descomposición del nodo MMS ──
export function decomposeMms(node: string, lnClasses: string[]): Decomp | null {
  let n = (node || "").trim();
  if (!n || n === "N/A" || n === "—") return null;
  // Paso 6: 3 barras → la última se convierte en "."
  const slashes = (n.match(/\//g) ?? []).length;
  if (slashes === 3) {
    const i = n.lastIndexOf("/");
    n = n.slice(0, i) + "." + n.slice(i + 1);
  } else if (slashes !== 2) {
    return null;
  }
  const p1 = n.indexOf("/"), p2 = n.indexOf("/", p1 + 1);
  const ldInst = n.slice(0, p1);
  const logical = n.slice(p1 + 1, p2);
  const doName = n.slice(p2 + 1);
  if (!ldInst || !logical || !doName) return null;
  if (logical === "LLN0") return { ldInst, prefix: "", lnClass: "LLN0", lnInst: "", doName };
  // Pasos 9-11: buscar la clase más larga contenida en el nodo lógico
  let best = ""; let bestIdx = -1;
  for (const c of lnClasses) {
    const idx = logical.indexOf(c);
    if (idx >= 0 && c.length > best.length) { best = c; bestIdx = idx; }
  }
  if (!best) return null;
  const prefix = logical.slice(0, bestIdx);
  const lnInst = logical.slice(bestIdx + best.length);
  if (lnInst && !/^\d+$/.test(lnInst)) return null;  // instancia no numérica → formato inválido
  return { ldInst, prefix, lnClass: best, lnInst, doName };
}

function childByTag(parent: Element, tag: string): Element | null {
  for (const el of Array.from(parent.children)) if (el.localName === tag) return el;
  return null;
}
function childrenByTag(parent: Element, tag: string): Element[] {
  return Array.from(parent.children).filter(el => el.localName === tag);
}

// Indentación: replica la sangría de los hijos existentes de un elemento
function indentOf(parent: Element): { pad: string; padChild: string } {
  const doc = parent.ownerDocument;
  let pad = "\n\t";
  for (const n of Array.from(parent.childNodes)) {
    if (n.nodeType === 3 && /\n[\t ]+/.test(n.textContent ?? "")) {
      const m = (n.textContent ?? "").match(/\n([\t ]+)$/);
      if (m) { pad = "\n" + m[1]; break; }
    }
  }
  const unit = pad.includes("\t") ? "\t" : "  ";
  void doc;
  return { pad, padChild: pad + unit };
}

// Inserta `el` dentro de `parent` justo antes de `before` (o al final), con sangría
function insertWithIndent(parent: Element, el: Element, before: Element | null) {
  const doc = parent.ownerDocument;
  const { pad } = indentOf(parent);
  const ws = doc.createTextNode(pad);
  if (before) { parent.insertBefore(ws, before); parent.insertBefore(el, before); }
  else {
    // antes del texto de cierre si existe
    const last = parent.lastChild;
    if (last && last.nodeType === 3 && /^\s*$/.test(last.textContent ?? "")) {
      parent.insertBefore(ws, last); parent.insertBefore(el, last);
    } else { parent.appendChild(ws); parent.appendChild(el); }
  }
}

// ── Etapas 2 y 3: edición del SCL (por IED destino de cada fila) ──
export function mapToScl(xmlText: string, rows: NbSignal[], iedFallback?: string,
                         rcParams?: Record<string, RcParams>): MapSummary {
  const doc = new DOMParser().parseFromString(xmlText, "application/xml");
  if (doc.getElementsByTagName("parsererror").length) throw new Error("XML inválido");
  const root = doc.documentElement;

  // Etapa 1 · Pasos 2-4: lnClasses desde DataTypeTemplates
  const lnClasses = new Set<string>(IEC_LN_FALLBACK);
  const dtt = childByTag(root, "DataTypeTemplates");
  if (dtt) for (const lnt of childrenByTag(dtt, "LNodeType")) {
    const c = lnt.getAttribute("lnClass"); if (c) lnClasses.add(c);
  }
  const lnList = Array.from(lnClasses);

  const iedEls = childrenByTag(root, "IED");
  if (!iedEls.length) throw new Error("El SCL no contiene elementos <IED>");
  const infoIeds: SclIedInfo[] = iedEls.map(e => {
    const lds: string[] = [];
    for (const ap of childrenByTag(e, "AccessPoint"))
      for (const server of childrenByTag(ap, "Server"))
        for (const ld of childrenByTag(server, "LDevice")) {
          const i = ld.getAttribute("inst"); if (i) lds.push(i);
        }
    return { name: e.getAttribute("name") ?? "", lDevices: lds };
  });

  // Agrupar filas: IED destino → dataset destino → filas
  const skipped: MapSummary["skipped"] = [];
  const porIed = new Map<string, Map<string, NbSignal[]>>();
  // Busca el LDevice del IED que contiene el LN indicado (autocorrección de ldInst).
  const buscarLdDelLn = (iedEl: Element, dec: Decomp): string => {
    for (const ap of childrenByTag(iedEl, "AccessPoint"))
      for (const server of childrenByTag(ap, "Server"))
        for (const ld of childrenByTag(server, "LDevice")) {
          const inst = ld.getAttribute("inst") ?? "";
          const hit = childrenByTag(ld, "LN").some(ln =>
            (ln.getAttribute("lnClass") ?? "") === dec.lnClass &&
            (ln.getAttribute("prefix")  ?? "") === (dec.prefix || "") &&
            (ln.getAttribute("inst")    ?? "") === (dec.lnInst || ""));
          if (hit) return inst;
        }
    return "";
  };
  for (const r of rows) {
    const dec = decomposeMms(r.mmsNode, lnList);
    if (!dec) { skipped.push({ signal: r.signal, node: r.mmsNode, reason: "nodo no interpretable" }); continue; }
    const iedName = iedFallback || suggestIed(r.mmsNode, infoIeds) || "";
    const iedEl = iedEls.find(e => e.getAttribute("name") === iedName || e.getAttribute("desc") === iedName);
    if (!iedEl) {
      skipped.push({ signal: r.signal, node: r.mmsNode, reason: "IED no encontrado: " + iedName }); continue;
    }
    // ── Validación del ldInst contra los LDevice reales del IED ──
    // Evita FCDAs con ldInst inexistente (violación del Keyref LDeviceInIEDKey).
    const nombreReal = iedEl.getAttribute("name") ?? iedName;
    const ldsValidos = new Set(infoIeds.find(i => i.name === nombreReal)?.lDevices ?? []);
    if (!ldsValidos.has(dec.ldInst)) {
      const corregido = dec.lnClass === "LLN0" ? "" : buscarLdDelLn(iedEl, dec);
      if (!corregido) {
        skipped.push({ signal: r.signal, node: r.mmsNode,
                       reason: `LDevice '${dec.ldInst}' no existe en ${nombreReal}` });
        continue;
      }
      // Persistir la corrección en el nodo (se re-descompone más adelante).
      r.mmsNode = corregido + r.mmsNode.slice(r.mmsNode.indexOf("/"));
    }
    const ds = (r.dsTarget ?? "").trim() || suggestDataset(r.mmsNode);
    if (!porIed.has(iedName)) porIed.set(iedName, new Map());
    const g = porIed.get(iedName)!;
    if (!g.has(ds)) g.set(ds, []);
    g.get(ds)!.push(r);
  }

  const AFTER_DATASET = ["ReportControl","LogControl","DOI","SettingControl","Inputs",
                         "GSEControl","SampledValueControl","SCLControl"];
  const iedResults: MapSummary["iedResults"] = [];
  let fcdaTotal = 0;

  for (const [iedName, grupos] of porIed) {
    const ied = iedEls.find(e => e.getAttribute("name") === iedName || e.getAttribute("desc") === iedName)!;

    // Pasos 3-6: LN0 destino (preferir el LDevice cuyo LN0 ya tenga DataSets)
    let ln0: Element | null = null; let ldChosen: Element | null = null;
    let ln0First: Element | null = null; let ldFirst: Element | null = null;
    for (const ap of childrenByTag(ied, "AccessPoint")) {
      const server = childByTag(ap, "Server"); if (!server) continue;
      for (const ld of childrenByTag(server, "LDevice")) {
        const cand = childByTag(ld, "LN0"); if (!cand) continue;
        if (!ln0First) { ln0First = cand; ldFirst = ld; }
        if (childrenByTag(cand, "DataSet").length) { ln0 = cand; ldChosen = ld; break; }
      }
      if (ln0) break;
    }
    if (!ln0) { ln0 = ln0First; ldChosen = ldFirst; }
    if (!ln0 || !ldChosen) {
      for (const [, filas] of grupos) for (const r of filas)
        skipped.push({ signal: r.signal, node: r.mmsNode, reason: "sin LN0 en IED " + iedName });
      continue;
    }
    const ldInst = ldChosen.getAttribute("inst") ?? "";
    const firstAfter = (tags: string[]): Element | null => {
      for (const el of Array.from(ln0!.children)) if (tags.includes(el.localName)) return el;
      return null;
    };

    const createdDatasets: string[] = [];
    const appendedDatasets: string[] = [];
    let fcdaCount = 0;

    for (const [dsName, filas] of grupos) {
      let ds = childrenByTag(ln0, "DataSet").find(d => d.getAttribute("name") === dsName) ?? null;
      let esNuevo = false;
      if (!ds) {
        ds = doc.createElementNS(SCL_NS, "DataSet");
        ds.setAttribute("name", dsName);
        insertWithIndent(ln0, ds, firstAfter(AFTER_DATASET));
        esNuevo = true;
      }
      const existentes = new Set(childrenByTag(ds, "FCDA").map(f =>
        [f.getAttribute("ldInst"), f.getAttribute("prefix") ?? "", f.getAttribute("lnClass"),
         f.getAttribute("lnInst") ?? "", f.getAttribute("doName"), f.getAttribute("fc")].join("|")));
      let agregados = 0;
      for (const r of filas) {
        const dec = decomposeMms(r.mmsNode, lnList)!;
        const fc = fcFor(dsName, dec.lnClass);
        const clave = [dec.ldInst, dec.prefix, dec.lnClass, dec.lnInst, dec.doName, fc].join("|");
        if (existentes.has(clave)) { skipped.push({ signal: r.signal, node: r.mmsNode, reason: "FCDA ya existente en " + iedName + "/" + dsName }); continue; }
        existentes.add(clave);
        const fcda = doc.createElementNS(SCL_NS, "FCDA");
        fcda.setAttribute("ldInst", dec.ldInst);
        if (dec.prefix) fcda.setAttribute("prefix", dec.prefix);
        fcda.setAttribute("lnClass", dec.lnClass);
        if (dec.lnInst) fcda.setAttribute("lnInst", dec.lnInst);   // Paso 9: omitir si vacío
        fcda.setAttribute("doName", dec.doName);
        fcda.setAttribute("fc", fc);
        insertWithIndent(ds, fcda, null);
        agregados++; fcdaCount++; fcdaTotal++;
      }
      if (esNuevo && agregados === 0) { ln0.removeChild(ds); continue; }
      if (esNuevo) createdDatasets.push(dsName); else if (agregados) appendedDatasets.push(dsName);
    }

    // Etapa 2 · Pasos 14-15: ReportControl por dataset NUEVO (defaults del prompt)
    const rcExistentes = new Set(childrenByTag(ln0, "ReportControl").map(r => r.getAttribute("name")));
    for (const dsName of createdDatasets) {
      let rcName = dsName + "_RPT"; let k = 1;
      while (rcExistentes.has(rcName)) rcName = dsName + "_RPT" + (++k);
      rcExistentes.add(rcName);
      const rc = doc.createElementNS(SCL_NS, "ReportControl");
      rc.setAttribute("name", rcName);
      rc.setAttribute("rptID", rcName);
      rc.setAttribute("datSet", dsName);
      rc.setAttribute("confRev", "1");
      const prm = rcParams?.[dsName];
      rc.setAttribute("buffered", (prm?.buffered ?? true) ? "true" : "false");
      const trg = doc.createElementNS(SCL_NS, "TrgOps");
      for (const [k, v] of Object.entries(prm?.trgOps ?? DEFAULT_TRGOPS))
        if (v) trg.setAttribute(k, "true");
      const opt = doc.createElementNS(SCL_NS, "OptFields");
      for (const [k, v] of Object.entries(prm?.optFields ?? DEFAULT_OPTFIELDS))
        if (v) opt.setAttribute(k, "true");
      const rpt = doc.createElementNS(SCL_NS, "RptEnabled");
      rpt.setAttribute("max", "5");
      for (let c = 1; c <= 5; c++) {
        const cl = doc.createElementNS(SCL_NS, "ClientLN");
        cl.setAttribute("iedName", "Client" + c);
        cl.setAttribute("apRef", "AP1");
        cl.setAttribute("ldInst", "LD0");
        cl.setAttribute("lnClass", "IHMI");
        cl.setAttribute("lnInst", "1");
        insertWithIndent(rpt, cl, null);
      }
      insertWithIndent(rc, trg, null);
      insertWithIndent(rc, opt, null);
      insertWithIndent(rc, rpt, null);
      insertWithIndent(ln0, rc, firstAfter(["LogControl","DOI","SettingControl","Inputs","GSEControl","SampledValueControl"]));
    }

    iedResults.push({ iedName, ldInst, createdDatasets, appendedDatasets, fcdaCount });
  }

  // Etapa 3: serializar preservando la declaración XML original y limpiar prefijos
  let out = new XMLSerializer().serializeToString(doc);
  const declMatch = xmlText.match(/^\s*<\?xml[^?]*\?>/);
  const decl = declMatch ? declMatch[0].trim() : '<?xml version="1.0" encoding="UTF-8"?>';
  if (!/^\s*<\?xml/.test(out)) out = decl + "\n" + out;
  out = out.replace(/\bns0:|\bns1:/g, "").replace(/xmlns:ns0="[^"]*"\s?|xmlns:ns1="[^"]*"\s?/g, "");
  return { xml: out, iedResults, fcdaCount: fcdaTotal, skipped };
}

// Descarga del archivo resultante
export function downloadScl(xml: string, baseName: string) {
  const blob = new Blob([xml], { type: "application/xml" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = baseName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
}
